import React, { useCallback, useMemo } from 'react';
import { cx } from '@linaria/core';
import { baseTheme } from 'styles/utils';
import Select, {
  ActionMeta,
  ClearIndicatorProps,
  components,
  DropdownIndicatorProps,
  GroupHeadingProps,
  GroupProps,
  InputProps,
  MenuListProps,
  NoticeProps,
  OptionProps,
  PlaceholderProps,
  SingleValueProps,
} from 'react-select';
import { InputActionMeta, OnChangeValue } from 'react-select/dist/declarations/src/types';
import { MenuPortalProps } from 'react-select/dist/declarations/src/components/Menu';
import { useRouter } from 'next/router';
import { CommunitySearchModal } from 'components/CommunitySearchModal';
import { SupportedListingEntities } from 'modules/Listings/types';
import { Button } from 'components/ui_library/Button';
import { useModalActions } from 'components/ModalManager/Context';
import { noop } from 'utils/fp';
import { FilterOptionOption } from 'react-select/dist/declarations/src/filters';
import { isUrl } from 'utils/url';
import { Tooltip } from 'components/PlatformResourceSelector/Tooltip';
import { Arrow } from 'components/PlatformResourceSelector/Arrow';
import {
  communitySearchButtonStyled,
  communitySearchModalStyled,
  controlIsClosedStyled,
  controlIsOpenStyled,
  controlStyled,
  createNewButtonStyled,
  createNewContainerStyled,
  disabledStyle,
  dropdownIndicatorStyled,
  expandButtonStyled,
  groupHeadingStyled,
  groupStyled,
  loadingMessageStyled,
  menuListStyled,
  noOptionMessageStyled,
  optionHoverStyled,
  optionStyled,
  placeholderStyled,
  singleValueStyled,
  valueContainerStyled,
} from 'components/PlatformResourceSelector/PlatformResourceSelector.styles';
import { GroupValue, OptionValue } from 'components/PlatformResourceSelector/usePlatformResourceSelector';
import { ResourceIcon } from 'components/PlatformResourceSelector/ResourceIcon';
import { CheckedIcon } from 'components/PlatformResourceSelector/CheckedIcon';
import { CircleLoader } from 'components/PlatformResourceSelector/CircleLoader';
import { LensIcon } from 'components/PlatformResourceSelector/LensIcon';
import { WarningIcon } from 'components/PlatformResourceSelector/WarningIcon';
import { extractModelIdsFromUrl, ResourceType } from 'components/PlatformResourceSelector/utils';

const MenuPortal = <MultiSelect extends boolean>(props: MenuPortalProps<OptionValue, MultiSelect, GroupValue>) => {
  return <components.MenuPortal {...props}>{props.children}</components.MenuPortal>;
};

const DropdownIndicator = <MultiSelect extends boolean>(props: DropdownIndicatorProps<OptionValue, MultiSelect, GroupValue>) => {
  return (
    <components.DropdownIndicator {...props}>
      <Arrow direction={props.selectProps.menuIsOpen ? 'up' : 'down'} />
    </components.DropdownIndicator>
  );
};

const IndicatorSeparator = () => null;

const LoadingIndicator = () => null;

const LoadingMessage = () => (
  <div className={loadingMessageStyled}>
    <CircleLoader />
  </div>
);

const HasErrorMessage = () => {
  return (
    <div className={noOptionMessageStyled}>
      <WarningIcon />
      <h2>There was an error loading the API</h2>
      <span>Please refresh the page and try again</span>
    </div>
  );
};

const NoOptionsMessage = <MultiSelect extends boolean>({ selectProps }: NoticeProps<OptionValue, MultiSelect, GroupValue>) => {
  // API error message
  if (selectProps.hasError) return <HasErrorMessage />;

  // No options message
  return (
    <div className={noOptionMessageStyled}>
      <LensIcon />
      <h2>No compatible resources found</h2>
      <span>We couldn’t find the resource you were looking for or you might not have access to it</span>
    </div>
  );
};

const CreateNewSection = ({ type }: { type?: ResourceType }) => {
  if (!type?.length) return null;

  const { userOrOrgId, appId } = useRouter().query as { userOrOrgId: string; appId: string };
  return (
    <div className={createNewContainerStyled}>
      {type === 'models/workflows' ? (
        <span className={createNewButtonStyled}>
          Create new
          <a href={`/${userOrOrgId}/${appId}/models/new`}>Model</a>
          or
          <a href={`/${userOrOrgId}/${appId}/workflows/new`}>Workflow</a>
        </span>
      ) : (
        <span className={createNewButtonStyled}>
          Create new
          <a href={`/${userOrOrgId}/${appId}/${type}/new`}>{type?.slice(0, type?.length - 1)}</a>
        </span>
      )}
    </div>
  );
};

const COMMUNITY_SEARCH_MODAL_ID = 'community_search_modal_id';
type CommunitySearchModalProps = {
  entityType: 'models' | 'workflows';
  onSelectItem: (entity: SupportedListingEntities) => void;
  modalParams?: string;
};

const CommunitySearchSection = ({ entityType, onSelectItem, modalParams = '' }: CommunitySearchModalProps) => {
  const { openModal, closeModal } = useModalActions();

  const onSelectItemFromModal = useCallback(
    (entity: SupportedListingEntities) => {
      onSelectItem(entity);
      closeModal({
        id: COMMUNITY_SEARCH_MODAL_ID,
      });
    },
    [onSelectItem],
  );

  const openCommunitySearchModal = (): void => {
    const onCloseModal = () => {
      closeModal({
        id: COMMUNITY_SEARCH_MODAL_ID,
      });
    };

    openModal({
      className: communitySearchModalStyled,
      content: (
        <CommunitySearchModal onSelect={onSelectItemFromModal} segment={entityType} modelsConfig={{ params: modalParams }} onClose={onCloseModal} />
      ),
      id: COMMUNITY_SEARCH_MODAL_ID,
      makeActions: noop,
      title: '',
    });
  };

  return (
    <div className={communitySearchButtonStyled}>
      <Button variant="unstyled" onClick={() => openCommunitySearchModal()}>
        Explore Community Models / Workflows
      </Button>
    </div>
  );
};

const FooterComponent = <MultiSelect extends boolean>(props: MenuListProps<OptionValue, MultiSelect, GroupValue>): JSX.Element | null => {
  const { communitySearchModal, canCreate, footerContent, type } = props.selectProps;
  if (footerContent) return footerContent;
  return (
    <>
      {communitySearchModal && <CommunitySearchSection {...communitySearchModal} />}
      {canCreate && <CreateNewSection type={type} />}
    </>
  );
};

const MenuList = <MultiSelect extends boolean>(props: MenuListProps<OptionValue, MultiSelect, GroupValue>) => {
  return (
    <div>
      <components.MenuList {...props} />
      {!props.selectProps.isLoading && <FooterComponent {...props} />}
    </div>
  );
};

const GroupHeading = <MultiSelect extends boolean>(props: GroupHeadingProps<OptionValue, MultiSelect, GroupValue>) => {
  return (
    <components.GroupHeading {...props} className={cx(groupHeadingStyled, props.data.type && props.data.type)}>
      {props.children}
    </components.GroupHeading>
  );
};

const Group = <MultiSelect extends boolean>({ children, ...props }: GroupProps<OptionValue, MultiSelect, GroupValue>) => {
  const maxOptionsToShow = props?.data?.label === 'Featured Models' ? 20 : 5;

  return (
    <div className={groupStyled}>
      <components.Group {...props}>{Array.isArray(children) && !props.data.expand ? children.slice(0, maxOptionsToShow) : children}</components.Group>
      {!props.data.expand && props.options.length > maxOptionsToShow && (
        <button
          type="button"
          className={expandButtonStyled}
          onClick={() => {
            props.selectProps.onExpandGroup?.(props.data.label);
          }}
        >{`See all ${props.options.length} results`}</button>
      )}
    </div>
  );
};

const Option = <MultiSelect extends boolean>(props: OptionProps<OptionValue, MultiSelect, GroupValue>) => {
  // 🔥 Featured Items:
  if (props.data.featured) {
    return (
      <components.Option {...props} className={cx(optionStyled, (props.isFocused || props.isSelected) && optionHoverStyled)}>
        {typeof props.data.icon === 'function' ? <props.data.icon className="icon" size={40} /> : props.data.icon}
        <p className="information">
          <span className="label" style={{ fontWeight: 400 }}>
            {props.data.label}
          </span>
        </p>
        {props.isSelected && <CheckedIcon />}
      </components.Option>
    );
  }

  // Default Template:
  const optionContent = useMemo(() => {
    return (
      <p className="information">
        <span className="label">{props.data.label}</span>
        <span className="sub-label">
          {props.data.entity?.user_id}/{props.data.entity?.app_id}
        </span>
      </p>
    );
  }, [props.data.label, props.data.tooltip]);

  const tooltipContent = useMemo(() => {
    return props.data.tooltip ? (
      <Tooltip initialState={{ placement: 'right' }} text={props.data.tooltip}>
        {optionContent}
      </Tooltip>
    ) : (
      optionContent
    );
  }, [props.data.label, props.data.tooltip]);

  return (
    <components.Option {...props} className={cx(optionStyled, (props.isFocused || props.isSelected) && optionHoverStyled)}>
      <div className="resource-icon">
        <ResourceIcon type={props.data.type} color={baseTheme.light.colors.gray700} />
      </div>
      {tooltipContent}
      {props.isSelected && <CheckedIcon />}
    </components.Option>
  );
};

const SingleValue = <MultiSelect extends boolean>(props: SingleValueProps<OptionValue, MultiSelect, GroupValue>) => {
  const iconToRender = useMemo(() => {
    if (props.data.featured) {
      return typeof props.data.icon === 'function' ? <props.data.icon /> : props.data.icon;
    } else {
      return <ResourceIcon type={props.data.type} color={baseTheme.light.colors.gray700} />;
    }
  }, [props.data.featured, props.data.icon, props.data.type]);

  return (
    <components.SingleValue {...props} className={singleValueStyled}>
      {iconToRender}
      <span title={props.data.label}>{props.data.label}</span>
    </components.SingleValue>
  );
};

const Placeholder = <MultiSelect extends boolean>(props: PlaceholderProps<OptionValue, MultiSelect, GroupValue>) => {
  return (
    <components.Placeholder {...props} className={placeholderStyled}>
      {props.children}
    </components.Placeholder>
  );
};

const ClearIndicator = <MultiSelect extends boolean>(props: ClearIndicatorProps<OptionValue, MultiSelect, GroupValue>) => {
  return <components.ClearIndicator {...props} />;
};

const customFilterOption = (candidate: FilterOptionOption<OptionValue>, input: string) => {
  if (!input.length) return true;

  if (isUrl(input)) {
    // WARNING — only works for models urls!
    const { modelId } = extractModelIdsFromUrl(input);
    if (modelId) return candidate.value.toLowerCase().includes(modelId.toLowerCase());
  }

  return candidate.value.toLowerCase().includes(input.toLowerCase());
};

const CustomInput = <MultiSelect extends boolean>(props: InputProps<OptionValue, MultiSelect, GroupValue>) => {
  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    event.stopPropagation();
  };
  return <components.Input {...props} onKeyDown={handleKeyDown} />;
};

export interface PlatformResourceSelectorProps<MultiSelect extends boolean = false> {
  canCreate?: boolean;
  communitySearchModal?: {
    entityType: 'models' | 'workflows';
    onSelectItem: (entity: SupportedListingEntities) => void;
    modalParams?: string;
  };
  controlClassName?: string;
  expandGroup?: (group?: string) => void;
  footerContent?: JSX.Element;
  hasError?: boolean;
  isClearable?: boolean;
  isDisabled?: boolean;
  isLoading?: boolean;
  menuPortalTarget?: HTMLElement;
  multiSelect?: MultiSelect;
  onBlur?: () => void;
  onChange?: (value: OnChangeValue<OptionValue, MultiSelect>, actionMeta: ActionMeta<OptionValue>) => void;
  onFocus?: () => void;
  onInputChange?: (newValue: string, actionMeta: InputActionMeta) => void;
  options: GroupValue[];
  placeholder?: string;
  type: ResourceType;
  value?: OptionValue | null;
}

export const PlatformResourceSelector = <MultiSelect extends boolean = false>({
  canCreate,
  controlClassName,
  communitySearchModal,
  expandGroup,
  footerContent,
  hasError,
  isClearable,
  isDisabled,
  isLoading,
  menuPortalTarget,
  multiSelect,
  onBlur,
  onChange,
  onFocus,
  onInputChange,
  options,
  placeholder = 'Search or enter URL',
  type,
  value,
}: PlatformResourceSelectorProps<MultiSelect>) => {
  return (
    <Select
      classNames={{
        group: () => groupStyled,
        menuList: () => menuListStyled,
        dropdownIndicator: () => dropdownIndicatorStyled,
        valueContainer: () => valueContainerStyled,
        control: (state) =>
          cx(controlStyled, state.isFocused ? controlIsOpenStyled : controlIsClosedStyled, controlClassName, state.isDisabled && disabledStyle),
      }}
      type={type}
      options={options}
      hasError={hasError}
      canCreate={canCreate}
      isMulti={multiSelect}
      isLoading={isLoading}
      isDisabled={isDisabled}
      isClearable={isClearable}
      placeholder={placeholder}
      value={value || null}
      aria-label={`Select ${type}`}
      footerContent={footerContent}
      communitySearchModal={communitySearchModal}
      minMenuHeight={400}
      menuPlacement="auto"
      menuPortalTarget={menuPortalTarget}
      onBlur={onBlur}
      onFocus={onFocus}
      onChange={onChange}
      onExpandGroup={expandGroup}
      onInputChange={onInputChange}
      filterOption={customFilterOption}
      components={{
        Input: CustomInput,
        ClearIndicator,
        DropdownIndicator,
        Group,
        GroupHeading,
        IndicatorSeparator,
        LoadingIndicator,
        LoadingMessage,
        MenuList,
        MenuPortal,
        NoOptionsMessage,
        Option,
        Placeholder,
        SingleValue,
      }}
    />
  );
};
